import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useSelector } from 'react-redux';
import api from '../../utils/functions/api';
import { accessRightSubMenu } from '../../utils/functions/accessRight';
import { AccessDenied } from '../../components/Error';
import { confirmAsync, showAlert } from '../../utils/functions/globalDialog';
import {
  CATEGORY_OPTIONS,
  PRIORITY_OPTIONS,
  findOption,
  fmtDate,
  fmtDateTime,
} from './announcement/constants';
import AnnouncementFormDialog from './announcement/AnnouncementFormDialog';
import AnnouncementReadReportDialog from './announcement/AnnouncementReadReportDialog';

import { brandHex } from '../../utils/orgConfig';
const apiBase = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 10;

// การ์ดสรุปด้านบน — กดเพื่อกรองตามสถานะ (แทนแท็บ)
const STATUS_CARDS = [
  { value: 'active', countKey: 'active', label: 'กำลังประกาศ', color: '#10b981', Icon: PodcastsIcon },
  { value: 'expired', countKey: 'expired', label: 'หมดอายุ', color: '#f59e0b', Icon: EventBusyOutlinedIcon },
  { value: 'closed', countKey: 'closed', label: 'ปิดแล้ว', color: '#6b7280', Icon: DoNotDisturbOnOutlinedIcon },
  { value: '', countKey: 'all', label: 'ทั้งหมด', get color() { return brandHex(500); }, Icon: InventoryOutlinedIcon },
];

const rowStatus = (row) => {
  if (row.status === 'closed') return { label: 'ปิดแล้ว', color: '#6b7280' };
  if (row.end_at && new Date(row.end_at) < new Date()) return { label: 'หมดอายุ', color: '#f59e0b' };
  return { label: 'กำลังประกาศ', color: '#10b981' };
};

const audienceLabel = (a) => {
  if (a.audience_type === 'department') {
    return `แผนก: ${(a.departments || []).map((d) => d.name).join(', ')}`;
  }
  if (a.audience_type === 'role') {
    return `บทบาท: ${(a.roles || []).map((r) => r.name).join(', ')}`;
  }
  return 'พนักงานทุกคน';
};

function StatCard({
  card, count, selected, onClick,
}) {
  const { Icon } = card;
  return (
    <Card
      onClick={onClick}
      elevation={0}
      sx={{
        p: 2,
        cursor: 'pointer',
        border: '1.5px solid',
        borderColor: selected ? card.color : 'transparent',
        bgcolor: selected ? alpha(card.color, 0.06) : 'background.paper',
        boxShadow: selected ? `0 4px 14px ${alpha(card.color, 0.18)}` : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'all 0.15s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 16px ${alpha(card.color, 0.16)}` },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(card.color, 0.12),
            color: card.color,
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box minWidth={0}>
          <Typography variant="h5" fontWeight={700} lineHeight={1.1}>
            {count ?? <Skeleton width={28} />}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{card.label}</Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function MetaItem({ icon: Icon, children, title }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: '100%' }} title={title}>
      <Icon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" noWrap>{children}</Typography>
    </Stack>
  );
}

function AnnouncementCard({
  row, onEdit, onToggle, onDelete, onReport,
}) {
  const cat = findOption(CATEGORY_OPTIONS, row.category);
  const pri = findOption(PRIORITY_OPTIONS, row.priority);
  const st = rowStatus(row);
  const closed = row.status === 'closed';
  const pct = row.target_count ? Math.round((row.ack_count / row.target_count) * 100) : 0;
  const ackColor = pct >= 100 ? '#10b981' : brandHex(500);

  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        borderLeft: `5px solid ${cat.color}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        opacity: closed ? 0.65 : 1,
        transition: 'box-shadow 0.15s ease',
        '&:hover': { boxShadow: '0 6px 20px rgba(17,24,39,0.08)' },
      }}
    >
      <Box sx={{ p: 2, flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <Chip
            size="small"
            label={cat.label}
            sx={{ bgcolor: alpha(cat.color, 0.1), color: cat.color, fontWeight: 600 }}
          />
          {row.priority !== 'normal' && <Chip size="small" label={pri.label} color={pri.color} />}
          <Chip
            size="small"
            variant="outlined"
            label={st.label}
            sx={{ color: st.color, borderColor: alpha(st.color, 0.4), fontWeight: 500 }}
          />
          {row.pinned && (
            <Tooltip title="ปักหมุดไว้บนสุด">
              <PushPinIcon sx={{ fontSize: 17, color: 'primary.main' }} />
            </Tooltip>
          )}
          <Box flexGrow={1} />
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="แก้ไข">
              <IconButton size="small" onClick={() => onEdit(row)}><EditOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title={closed ? 'เปิดประกาศอีกครั้ง' : 'ปิดประกาศ'}>
              <IconButton size="small" onClick={() => onToggle(row)}>
                {closed ? <ReplayIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="ลบ">
              <IconButton size="small" onClick={() => onDelete(row)} sx={{ '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Typography
          onClick={() => onEdit(row)}
          sx={{
            mt: 1, fontWeight: 700, fontSize: '1.02rem', cursor: 'pointer', '&:hover': { color: 'primary.main' },
          }}
        >
          {row.title}
        </Typography>
        {row.body && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'pre-line',
            }}
          >
            {row.body}
          </Typography>
        )}

        <Stack direction="row" sx={{ mt: 1.5, flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.5 }}>
          <MetaItem icon={GroupsOutlinedIcon} title={audienceLabel(row)}>{audienceLabel(row)}</MetaItem>
          <MetaItem icon={EventOutlinedIcon}>
            {row.end_at ? `หมดอายุ ${fmtDate(row.end_at)}` : 'ไม่มีวันหมดอายุ'}
          </MetaItem>
          {row.attachments?.length > 0 && (
            <MetaItem icon={AttachFileIcon}>{`${row.attachments.length} ไฟล์แนบ`}</MetaItem>
          )}
          <MetaItem icon={PersonOutlineIcon}>
            {`${row.created_by_name || '-'} · ${fmtDateTime(row.createdAt)}`}
          </MetaItem>
        </Stack>
      </Box>

      <Box
        onClick={() => onReport(row)}
        sx={{
          width: { xs: 'auto', md: 190 },
          flexShrink: 0,
          px: 2.5,
          py: 2,
          cursor: 'pointer',
          borderLeft: { md: '1px solid' },
          borderTop: { xs: '1px solid', md: 'none' },
          borderColor: { xs: 'divider', md: 'divider' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: alpha(ackColor, 0.05) },
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">รับทราบแล้ว</Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: ackColor }}>{pct}%</Typography>
        </Stack>
        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', my: 0.25 }}>
          {row.ack_count}
          <Typography component="span" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
            {` / ${row.target_count} คน`}
          </Typography>
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(pct, 100)}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(ackColor, 0.12),
            '& .MuiLinearProgress-bar': { bgcolor: ackColor, borderRadius: 3 },
          }}
        />
        <Typography variant="caption" color="primary" sx={{ mt: 0.75 }}>ดูรายชื่อ →</Typography>
      </Box>
    </Card>
  );
}

function CardSkeleton() {
  return (
    <Card elevation={0} sx={{ p: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={90} height={24} />
        <Skeleton variant="rounded" width={70} height={24} />
      </Stack>
      <Skeleton width="45%" height={28} sx={{ mt: 1 }} />
      <Skeleton width="80%" />
      <Skeleton width="35%" />
    </Card>
  );
}

// หน้าจัดการประกาศจากบริษัทถึงพนักงาน (/humen/announcements) — ใครเข้าเมนูนี้ได้ก็สร้าง/แก้ไข/ปิดประกาศได้
// (เกณฑ์เดียวกับ requireSubMenuAccess ฝั่ง backend — layout HR เช็คแค่ระดับโมดูล เลยต้องกันซ้ำที่หน้านี้)
export default function HrAnnouncements({ title }) {
  const me = useSelector((state) => state.me);
  const canManage = !!me?.userData?.permissions?.admin || accessRightSubMenu(me, 'HUMEN', '/humen/announcements');

  const [status, setStatus] = useState('active');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ rows: [], total: 0, counts: null });
  const [loading, setLoading] = useState(true);

  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reportOf, setReportOf] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`${apiBase}/announcement`, {
        params: {
          status, category, search: search.trim(), page, size: PAGE_SIZE,
        },
      });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [status, category, search, page]);

  useEffect(() => {
    if (!canManage) return undefined;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load, canManage]);

  useEffect(() => {
    if (!canManage) return;
    api.get(`${apiBase}/department`).then(({ data: res }) => setDepartments(res?.rows || []));
    api.get(`${apiBase}/role`).then(({ data: res }) => setRoles(res?.rows || []));
  }, [canManage]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const toggleClosed = async (row) => {
    const closing = row.status === 'active';
    const ok = await confirmAsync(closing
      ? `ปิดประกาศ "${row.title}" ? (จะไม่ขึ้น popup ให้พนักงานเห็นอีก)`
      : `เปิดประกาศ "${row.title}" อีกครั้ง?`);
    if (!ok) return;
    await api.put(`${apiBase}/announcement/${row.id}`, { status: closing ? 'closed' : 'active' });
    load();
  };

  const handleBroadcastTest = async () => {
    const ok = await confirmAsync('ส่งแจ้งเตือนทดสอบถึงทุกคนที่เปิดรับการแจ้งเตือนไว้?');
    if (!ok) return;
    const { data: res } = await api.post(`${apiBase}/announcement/broadcast-test`);
    showAlert(`ส่งแจ้งเตือนทดสอบแล้ว ${res?.success ?? 0} / ${res?.total ?? 0} เครื่อง`);
  };

  const handleDelete = async (row) => {
    const ok = await confirmAsync(`ลบประกาศ "${row.title}" ถาวร? ประวัติการรับทราบจะถูกลบไปด้วย`);
    if (!ok) return;
    await api.delete(`${apiBase}/announcement/${row.id}`);
    load();
  };

  if (!canManage) {
    return <AccessDenied moduleLabel="ประกาศ" />;
  }

  const pageCount = Math.max(Math.ceil((data.total || 0) / PAGE_SIZE), 1);
  const showSkeleton = loading && data.rows.length === 0;
  const filtered = !!(category || search.trim());

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* หัวหน้า */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexGrow={1}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              background: 'linear-gradient(135deg, rgb(var(--color-primary-450)) 0%, rgb(var(--color-primary-500)) 60%, rgb(var(--color-primary-600)) 100%)',
              boxShadow: '0 6px 16px rgb(var(--color-primary-500) / 0.35)',
            }}
          >
            <CampaignIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{title || 'ประกาศ'}</Typography>
            <Typography variant="body2" color="text.secondary">
              ประกาศจากบริษัทถึงพนักงาน — ขึ้นเป็น popup ทุกครั้งที่พนักงานเปิดเว็บ
            </Typography>
          </Box>
        </Stack>
        <Button variant="outlined" startIcon={<CampaignIcon />} onClick={handleBroadcastTest} sx={{ px: 2.5, py: 1 }}>
          ทดสอบ Broadcast
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ px: 2.5, py: 1, boxShadow: '0 4px 12px rgb(var(--color-primary-500) / 0.3)' }}
        >
          สร้างประกาศ
        </Button>
      </Stack>

      {/* การ์ดสรุป / ตัวกรองสถานะ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        {STATUS_CARDS.map((c) => (
          <StatCard
            key={c.countKey}
            card={c}
            count={data.counts?.[c.countKey]}
            selected={status === c.value}
            onClick={() => { setStatus(c.value); setPage(1); }}
          />
        ))}
      </Box>

      {/* ตัวกรองหมวด + ค้นหา */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        spacing={1.5}
        mb={2}
      >
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }} flexGrow={1}>
          <Chip
            label="ทุกหมวด"
            onClick={() => { setCategory(''); setPage(1); }}
            color={category === '' ? 'primary' : 'default'}
            variant={category === '' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
          {CATEGORY_OPTIONS.map((c) => {
            const active = category === c.value;
            return (
              <Chip
                key={c.value}
                label={c.label}
                onClick={() => { setCategory(active ? '' : c.value); setPage(1); }}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  color: active ? '#fff' : c.color,
                  bgcolor: active ? c.color : 'transparent',
                  borderColor: alpha(c.color, 0.45),
                  '&:hover': { bgcolor: active ? c.color : alpha(c.color, 0.08) },
                  '&.MuiChip-clickable:hover': { bgcolor: active ? c.color : alpha(c.color, 0.08) },
                }}
              />
            );
          })}
        </Stack>
        <TextField
          size="small"
          placeholder="ค้นหาหัวข้อประกาศ"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ width: { xs: '100%', md: 280 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* รายการประกาศ */}
      <Stack spacing={1.5} sx={{ opacity: loading && !showSkeleton ? 0.6 : 1, transition: 'opacity 0.15s' }}>
        {showSkeleton && [0, 1, 2].map((i) => <CardSkeleton key={i} />)}

        {!loading && data.rows.length === 0 && (
          <Card elevation={0} sx={{ py: 7, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 2,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgb(var(--color-primary-500) / 0.08)',
                color: 'primary.main',
              }}
            >
              <CampaignIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography fontWeight={600}>
              {filtered ? 'ไม่พบประกาศที่ตรงกับตัวกรอง' : 'ยังไม่มีประกาศในสถานะนี้'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
              {filtered ? 'ลองเปลี่ยนหมวดหรือคำค้นหา' : 'สร้างประกาศแรกเพื่อแจ้งข่าวสารถึงพนักงาน'}
            </Typography>
            {!filtered && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>สร้างประกาศ</Button>
            )}
          </Card>
        )}

        {data.rows.map((row) => (
          <AnnouncementCard
            key={row.id}
            row={row}
            onEdit={openEdit}
            onToggle={toggleClosed}
            onDelete={handleDelete}
            onReport={setReportOf}
          />
        ))}
      </Stack>

      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_e, p) => setPage(p)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}

      <AnnouncementFormDialog
        open={formOpen}
        announcement={editing}
        departments={departments}
        roles={roles}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
      />
      <AnnouncementReadReportDialog announcement={reportOf} onClose={() => setReportOf(null)} />
    </Box>
  );
}
